import React from 'react';
import { Upload as AntUpload, UploadProps as AntUploadProps } from 'antd';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';

export interface UploadProps extends AntUploadProps {
  className?: string;
}

const useStyles = createUseStyles((theme: ThemeType) => ({
  upload: {
    '& .ant-upload.ant-upload-select': {
      backgroundColor: `${theme.color.semantic.background.neutral.primary} !important`,
      color: `${theme.color.semantic.brand.default.primary} !important`,
      fontSize: theme.typography.fontSize.sm,
      borderRadius: theme.radius.sm,
      fontWeight: theme.typography.fontWeight.regular,
      borderWidth: theme.border.width.sm,
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        backgroundColor: `${theme.color.semantic.brand.default.subtle} !important`,
        color: `${theme.color.semantic.brand.default.secondary} !important`,
        borderColor: `${theme.color.semantic.brand.default.secondary} !important`,
      },
    },
    '& .ant-upload.ant-upload-select.ant-upload-disabled': {
      backgroundColor: `${theme.color.semantic.background.neutral.soft} !important`,
      color: `${theme.color.semantic.text.soft} !important`,
      borderColor: `${theme.color.semantic.border.subtle} !important`,
      cursor: 'not-allowed',
      '&:hover': {
        backgroundColor: `${theme.color.semantic.background.neutral.soft} !important`,
        color: `${theme.color.semantic.text.soft} !important`,
        borderColor: `${theme.color.semantic.border.subtle} !important`,
      },
    },
    '& .ant-upload-list-item': {
      backgroundColor: `${theme.color.semantic.background.neutral.secondary} !important`,
      color: `${theme.color.semantic.text.primary} !important`,
      borderColor: `${theme.color.semantic.border.subtle} !important`,
      padding: `${theme.spacing[1]} ${theme.spacing[2]}`,
      fontSize: theme.typography.fontSize.sm,
      borderRadius: theme.radius.sm,
      borderWidth: theme.border.width.sm,
      borderStyle: 'solid',
      marginTop: theme.spacing[2],
      '& .anticon': {
        color: `${theme.color.semantic.text.secondary} !important`,
      },
      '&:hover': {
        backgroundColor: `${theme.color.semantic.background.neutral.subtle} !important`,
        '& .ant-upload-list-item-info': {
          backgroundColor: `${theme.color.semantic.background.neutral.subtle} !important`,
        },
        '& .ant-upload-list-item-name': {
          color: `${theme.color.semantic.text.primary} !important`,
        },
        '& .anticon': {
          color: `${theme.color.semantic.text.secondary} !important`,
        },
      },
    },
    '& .ant-upload-list-item-done': {
      '& .ant-upload-list-item-name': {
        color: `${theme.color.semantic.text.primary} !important`,
      },
      '& .anticon-check-circle': {
        color: `${theme.color.semantic.accent.green.strong} !important`,
      },
    },
    '& .ant-upload-list-item-error': {
      '& .ant-upload-list-item-name': {
        color: `${theme.color.semantic.text.primary} !important`,
      },
      '& .anticon-close-circle': {
        color: `${theme.color.semantic.brand.danger.primary} !important`,
      },
      '& .ant-upload-list-item-card-actions .anticon': {
        color: `${theme.color.semantic.brand.danger.primary} !important`,
      },
    },
    '& .ant-upload-list-item-uploading': {
      '& .ant-upload-list-item-progress': {
        '& .ant-progress-bg': {
          backgroundColor: `${theme.color.semantic.brand.default.primary} !important`,
        },
      },
    },
    '& .ant-upload-list-item-actions': {
      '& .anticon-delete': {
        color: `${theme.color.semantic.brand.danger.primary} !important`,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: `${theme.color.semantic.brand.danger.subtle} !important`,
          borderRadius: theme.radius.sm,
        },
      },
      '& .anticon-download': {
        color: `${theme.color.semantic.brand.default.primary} !important`,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: `${theme.color.semantic.brand.default.subtle} !important`,
          borderRadius: theme.radius.sm,
        },
      },
    },
  },
  uploadDragger: {
    '&.ant-upload.ant-upload-drag': {
      background: `${theme.color.semantic.background.neutral.subtle} !important`,
      backgroundColor: `${theme.color.semantic.background.neutral.subtle} !important`,
      borderColor: `${theme.color.semantic.border.subtle} !important`,
      borderStyle: 'dashed !important' as any,
      borderWidth: `${theme.border.width.sm} !important`,
      borderRadius: `${theme.radius.md} !important`,
      padding: `${theme.spacing[4]} !important`,
      transition: 'all 0.2s ease-in-out',
      '& .ant-upload-drag-container': {
        '& .anticon': {
          color: `${theme.color.semantic.text.secondary} !important`,
          fontSize: theme.typography.fontSize['2xl'],
        },
        '& .ant-upload-text': {
          color: `${theme.color.semantic.text.primary} !important`,
          fontSize: theme.typography.fontSize.sm,
          fontWeight: theme.typography.fontWeight.medium,
        },
        '& .ant-upload-hint': {
          color: `${theme.color.semantic.text.secondary} !important`,
          fontSize: theme.typography.fontSize.xs,
        },
      },
      '&:hover': {
        backgroundColor: `${theme.color.semantic.brand.default.subtle} !important`,
        borderColor: `${theme.color.semantic.brand.default.secondary} !important`,
        '& .ant-upload-drag-container': {
          '& .anticon': {
            color: `${theme.color.semantic.brand.default.secondary} !important`,
          },
          '& .ant-upload-text': {
            color: `${theme.color.semantic.brand.default.secondary} !important`,
          },
        },
      },
      '&.ant-upload-drag-hover': {
        backgroundColor: `${theme.color.semantic.brand.default.light} !important`,
        borderColor: `${theme.color.semantic.brand.default.primary} !important`,
        borderWidth: theme.border.width.md,
        '& .ant-upload-drag-container': {
          '& .anticon': {
            color: `${theme.color.semantic.brand.default.primary} !important`,
          },
          '& .ant-upload-text': {
            color: `${theme.color.semantic.brand.default.primary} !important`,
          },
        },
      },
    },
    '&.ant-upload-disabled': {
      backgroundColor: `${theme.color.semantic.background.neutral.soft} !important`,
      borderColor: `${theme.color.semantic.border.subtle} !important`,
      cursor: 'not-allowed',
      '& .ant-upload-drag-container': {
        '& .anticon': {
          color: `${theme.color.semantic.text.soft} !important`,
        },
        '& .ant-upload-text': {
          color: `${theme.color.semantic.text.soft} !important`,
        },
        '& .ant-upload-hint': {
          color: `${theme.color.semantic.text.soft} !important`,
        },
      },
      '&:hover': {
        backgroundColor: `${theme.color.semantic.background.neutral.soft} !important`,
        borderColor: `${theme.color.semantic.border.subtle} !important`,
      },
    },
    '& ~ .ant-upload-list': {
      '& .ant-upload-list-item': {
        backgroundColor: `${theme.color.semantic.background.neutral.secondary} !important`,
        color: `${theme.color.semantic.text.primary} !important`,
        borderColor: `${theme.color.semantic.border.subtle} !important`,
        padding: `${theme.spacing[1]} ${theme.spacing[2]}`,
        fontSize: theme.typography.fontSize.sm,
        borderRadius: theme.radius.sm,
        borderWidth: theme.border.width.sm,
        borderStyle: 'solid',
        marginTop: theme.spacing[2],
        '& .anticon': {
          color: `${theme.color.semantic.text.secondary} !important`,
        },
        '&:hover': {
          backgroundColor: `${theme.color.semantic.background.neutral.subtle} !important`,
          '& .ant-upload-list-item-info': {
            backgroundColor: `${theme.color.semantic.background.neutral.subtle} !important`,
          },
          '& .ant-upload-list-item-name': {
            color: `${theme.color.semantic.text.primary} !important`,
          },
          '& .anticon': {
            color: `${theme.color.semantic.text.secondary} !important`,
          },
        },
      },
      '& .ant-upload-list-item-done': {
        '& .ant-upload-list-item-name': {
          color: `${theme.color.semantic.text.primary} !important`,
        },
        '& .anticon-check-circle': {
          color: `${theme.color.semantic.accent.green.strong} !important`,
        },
      },
      '& .ant-upload-list-item-error': {
        '& .ant-upload-list-item-name': {
          color: `${theme.color.semantic.text.primary} !important`,
        },
        '& .anticon-close-circle': {
          color: `${theme.color.semantic.brand.danger.primary} !important`,
        },
        '& .ant-upload-list-item-card-actions .anticon': {
          color: `${theme.color.semantic.brand.danger.primary} !important`,
        },
      },
      '& .ant-upload-list-item-uploading': {
        '& .ant-upload-list-item-progress': {
          '& .ant-progress-bg': {
            backgroundColor: `${theme.color.semantic.brand.default.primary} !important`,
          },
        },
      },
      '& .ant-upload-list-item-actions': {
        '& .anticon-delete': {
          color: `${theme.color.semantic.brand.danger.primary} !important`,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: `${theme.color.semantic.brand.danger.subtle} !important`,
            borderRadius: theme.radius.sm,
          },
        },
        '& .anticon-download': {
          color: `${theme.color.semantic.brand.default.primary} !important`,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: `${theme.color.semantic.brand.default.subtle} !important`,
            borderRadius: theme.radius.sm,
          },
        },
      },
    },
  },
  uploadPictureCard: {
    '& .ant-upload.ant-upload-select-picture-card': {
      backgroundColor: `${theme.color.semantic.background.neutral.subtle} !important`,
      borderColor: `${theme.color.semantic.border.primary} !important`,
      borderWidth: theme.border.width.sm,
      borderStyle: 'solid',
      borderRadius: theme.radius.md,
      padding: theme.spacing[2],
      width: 104,
      height: 104,
      transition: 'all 0.2s ease-in-out',
      '& .ant-upload-text': {
        color: `${theme.color.semantic.text.secondary} !important`,
      },
      '&:hover': {
        backgroundColor: `${theme.color.semantic.background.neutral.soft} !important`,
        borderColor: `${theme.color.semantic.brand.default.primary} !important`,
        '& .ant-upload-text': {
          color: `${theme.color.semantic.brand.default.primary} !important`,
        },
      },
    },
    '& .ant-upload-list-picture-card-container': {
      width: 104,
      height: 104,
    },
    '& .ant-upload-list-item-uploading': {
      backgroundColor: `${theme.color.semantic.background.neutral.subtle} !important`,
      borderColor: `${theme.color.semantic.border.primary} !important`,
      '& .ant-upload-list-item-progress': {
        '& .ant-progress-bg': {
          backgroundColor: `${theme.color.semantic.brand.default.primary} !important`,
        },
      },
    },
  },
}));

export const Upload: React.FC<UploadProps> & {
  Dragger: typeof AntUpload.Dragger;
} = ({ className = '', listType = 'text', ...rest }) => {
  const classes = useStyles();

  const getClassName = () => {
    if (listType === 'picture-card') {
      return `${classes.uploadPictureCard} ${className}`.trim();
    }
    return `${classes.upload} ${className}`.trim();
  };

  return <AntUpload className={getClassName()} listType={listType} {...rest} />;
};

const UploadDragger: React.FC<UploadProps> = ({ className = '', ...rest }) => {
  const classes = useStyles();
  const combinedClassName = `${classes.uploadDragger} ${className}`.trim();

  return <AntUpload.Dragger className={combinedClassName} {...rest} />;
};

Upload.Dragger = UploadDragger;

export default Upload;
